import { omit, pick } from "lodash-es";
import { all, call, delay, put, select, takeLatest } from "redux-saga/effects";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Route from "@mapmycustomers/shared/types/entity/Route";
import { CategorizedMapEntries, MapEntry } from "@mapmycustomers/shared/types/map";
import { ColorPinLegend, ShapePinLegend } from "@mapmycustomers/shared/types/map/PinLegend";
import Organization from "@mapmycustomers/shared/types/Organization";
import { OrganizationSize } from "@mapmycustomers/shared/types/OrganizationSize";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import { isEntityPin } from "@mapmycustomers/shared/util/assert";

import getOnRouteVisibleTypes from "@app/scene/routes/utils/getOnRouteVisibleTypes";
import injectRouteScope from "@app/scene/routes/utils/injectRouteScope";
import routeSettings from "@app/scene/routes/utils/routeSettings";
import { callApi } from "@app/store/api/callApi";
import { isEntityViewVisible } from "@app/store/entityView";
import { hideEntityView } from "@app/store/entityView/actions";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId, getOrganizationSize, getUserMetaData } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { boundariesViewSettingsChanged } from "@app/store/mapLayers/boundaries/actions";
import { getAllColorLegends, getAllShapeLegends } from "@app/store/pinLegends";
import GeoPath from "@app/types/GeoPath";
import MapPersistedViewportState from "@app/types/map/MapPersistedViewportState";
import MapViewportState from "@app/types/map/MapViewportState";
import buildPinLegends from "@app/util/map/buildPinLegends";
import categorizeMapEntries from "@app/util/map/categorizeMapEnties";
import convertViewportToPersist from "@app/util/map/convertViewportToPersist";
import createDefaultMapViewState from "@app/util/map/createDefaultMapViewState";
import getPrecisionDetails from "@app/util/map/getPrecisionDetails";
import convertMapFilterToPlatformFilterModel from "@app/util/viewModel/convertMapFilterToPlatformFilterModel";

import {
  applyRouteViewSettings,
  fetchPins,
  fetchRouteViewPins,
  focusPin,
  initializeRouteViewport,
  persistRouteViewport,
  refreshRouteLassoMode,
  setRouteFocusedStop,
  setRoutePinHover,
  setRouteSelectedPin,
  updateRouteLassoClusters,
} from "../../actions";
import {
  getOriginalRoute,
  getRoute,
  getRouteCategorizedExternalMapEntries,
  getRouteCategorizedMapEntries,
  getRouteLassoPaths,
  getRouteSelectedPin,
  getRouteViewport,
  getRouteViewState,
  isRouteLassoMode,
} from "../../selectors";

export function* onInitializeRouteViewport({
  payload: callback,
}: ReturnType<typeof initializeRouteViewport.request>) {
  try {
    const viewport: MapPersistedViewportState = yield call(routeSettings.getViewportState);
    yield call(callback, viewport);
    yield put(initializeRouteViewport.success());
  } catch (error) {
    yield put(initializeRouteViewport.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onPersistRouteViewport({ payload }: ReturnType<typeof persistRouteViewport>) {
  yield call(routeSettings.updateViewportState, convertViewportToPersist(payload));
}

export function* onFetchRouteViewPins({ payload }: ReturnType<typeof fetchRouteViewPins.request>) {
  try {
    if (payload.viewport) {
      yield put(persistRouteViewport(payload.viewport));
    }

    yield put(applyRouteViewSettings(payload.request));

    const viewState: MapViewState = yield select(getRouteViewState);

    if (payload.request.boundaryKey) {
      yield put(boundariesViewSettingsChanged({ boundaryKey: payload.request.boundaryKey }));
    }

    // Skip the metadata write when payload.request is empty — that means
    // this is a viewport-only refetch (pan/zoom) and viewState didn't change.
    if (Object.keys(payload.request).length) {
      yield put(updateMetadata.request({ routeViewSettings: omit(viewState, "lasso") }));
    }

    if (payload.updateOnly) {
      return;
    }

    const route: Route | undefined = yield select(getRoute);

    if (!route) {
      // Race on initial mount: the map fires bounds_changed before
      // initializeRouteView completes. Bail silently — the next
      // bounds_changed after the route loads will refetch.
      yield put(fetchRouteViewPins.failure(new Error("Route not loaded yet")));
      return;
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const organizationSize: OrganizationSize = yield select(getOrganizationSize);
    const colorPinLegends: ColorPinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: ShapePinLegend[] = yield select(getAllShapeLegends);

    // Read viewport from state so filter/colorKey changes (which don't carry a viewport
    // in their payload) still get viewport bounds. Mirrors the map scene saga pattern at
    // scene/map/store/mapMode/sagas.ts.
    const viewport: MapViewportState | undefined = yield select(getRouteViewport);

    const onRouteVisibleTypes = getOnRouteVisibleTypes(route, viewState.visibleEntities);

    let primaryEntries: CategorizedMapEntries = { clusters: [], entities: [], multiPins: [] };
    let primaryTotal = 0;

    if (onRouteVisibleTypes.length > 0) {
      const primaryFilter = injectRouteScope(
        viewState.filter,
        onRouteVisibleTypes,
        route.id,
        FilterOperator.IN_ANY
      );

      const primaryRequestPayload = {
        $filters: {
          bounds: viewport?.bounds,
          cadence: true,
          includeAccessStatus: true,
          includeCustomFields: true, // not used by web, but needed by platform to calculate coloring correctly
          pinLegends: buildPinLegends(
            onRouteVisibleTypes,
            viewState,
            colorPinLegends,
            shapePinLegends
          ),
          ...getPrecisionDetails(viewport?.zoom ?? 1, organizationSize),
          ...convertMapFilterToPlatformFilterModel(primaryFilter, onRouteVisibleTypes),
        },
      };

      const response: ListResponse<MapEntry> = yield callApi(
        "fetchMapPins",
        orgId,
        primaryRequestPayload
      );
      primaryEntries = categorizeMapEntries(response.data);
      primaryTotal = response.total;
    }

    let externalEntries: CategorizedMapEntries = { clusters: [], entities: [], multiPins: [] };

    if (viewState.showOther && viewState.visibleEntities.length > 0) {
      const externalFilter = injectRouteScope(
        viewState.filter,
        onRouteVisibleTypes,
        route.id,
        FilterOperator.NONE_OF
      );

      const externalRequestPayload = {
        $filters: {
          bounds: viewport?.bounds,
          cadence: true,
          includeAccessStatus: true,
          includeCustomFields: true, // not used by web, but needed by platform to calculate coloring correctly
          pinLegends: buildPinLegends(
            viewState.visibleEntities,
            viewState,
            colorPinLegends,
            shapePinLegends
          ),
          ...getPrecisionDetails(viewport?.zoom ?? 1, organizationSize),
          ...convertMapFilterToPlatformFilterModel(externalFilter, viewState.visibleEntities),
        },
      };

      const externalResponse: ListResponse<MapEntry> = yield callApi(
        "fetchMapPins",
        orgId,
        externalRequestPayload
      );
      externalEntries = categorizeMapEntries(externalResponse.data);
    }

    yield put(
      fetchRouteViewPins.success({
        ...primaryEntries,
        externalEntries,
        pinsCount: primaryTotal,
      })
    );
  } catch (error) {
    yield put(fetchRouteViewPins.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchPins({ payload }: ReturnType<typeof fetchPins.request>) {
  try {
    if (payload.viewport) {
      yield put(persistRouteViewport(payload.viewport));
    }

    yield put(applyRouteViewSettings(payload.request));

    const viewState: MapViewState = yield select(getRouteViewState);
    const route: Route | undefined = yield select(getOriginalRoute);

    if (payload.request.boundaryKey) {
      yield put(boundariesViewSettingsChanged({ boundaryKey: payload.request.boundaryKey }));
    }

    if (route && Object.keys(payload.request).length) {
      yield put(updateMetadata.request({ routeViewSettings: omit(viewState, "lasso") }));
    } else if (!route && (payload.request.colorKey || payload.request.shapeKey)) {
      // Create has no saved route, so it persists the legend alone, merged over the saved value.
      // Its other settings are seeded ephemerally — showOther is forced on and the saved filter is
      // not inherited — so writing them would clobber what View and Edit share.
      const userMetaData: User["metaData"] = yield select(getUserMetaData);
      yield put(
        updateMetadata.request({
          routeViewSettings: {
            ...(userMetaData.routeViewSettings ?? createDefaultMapViewState()),
            ...pick(viewState, ["colorKey", "shapeKey"]),
          },
        })
      );
    }

    if (payload.updateOnly) {
      return;
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const organizationSize: OrganizationSize = yield select(getOrganizationSize);
    const colorPinLegends: ColorPinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: ShapePinLegend[] = yield select(getAllShapeLegends);
    const viewport: MapViewportState | undefined = yield select(getRouteViewport);

    let externalEntries: CategorizedMapEntries = { clusters: [], entities: [], multiPins: [] };

    if (viewState.showOther && viewState.visibleEntities.length > 0) {
      // Edit: a saved route exists → exclude its own stops so they aren't drawn
      // twice (RouteFeature already draws them). Create: no saved route → no exclusion.
      const externalFilter = route
        ? injectRouteScope(
            viewState.filter,
            getOnRouteVisibleTypes(route, viewState.visibleEntities),
            route.id,
            FilterOperator.NONE_OF
          )
        : viewState.filter;

      const requestPayload = {
        $filters: {
          bounds: viewport?.bounds,
          cadence: true,
          includeAccessStatus: true,
          includeCustomFields: true,
          pinLegends: buildPinLegends(
            viewState.visibleEntities,
            viewState,
            colorPinLegends,
            shapePinLegends
          ),
          ...getPrecisionDetails(viewport?.zoom ?? 1, organizationSize),
          ...convertMapFilterToPlatformFilterModel(externalFilter, viewState.visibleEntities),
        },
      };

      const response: ListResponse<MapEntry> = yield callApi("fetchMapPins", orgId, requestPayload);
      externalEntries = categorizeMapEntries(response.data);
    }

    yield put(
      fetchPins.success({
        clusters: [],
        entities: [],
        externalEntries,
        multiPins: [],
        pinsCount: 0,
      })
    );
  } catch (error) {
    yield put(fetchPins.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFocusPin({ payload }: ReturnType<typeof focusPin>) {
  const orgId: Organization["id"] = yield select(getOrganizationId);

  try {
    const response: ListResponse<MapEntry> = yield callApi("fetchMapPins", orgId, {
      $filters: {
        entities: {
          [payload.entityType]: { $and: [{ id: { $in: [payload.id] } }] },
        },
        includeAccessStatus: true,
        precision: 20,
      },
      $limit: 1,
    });

    const pin = response.data.find(isEntityPin);

    if (!pin) {
      yield put(handleError({ error: new Error("Pin not found on map") }));
      return;
    }

    yield put(setRouteFocusedStop(pin));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

// Route pages open record previews through the global entity view (they don't register
// external preview like the Map scene does), so closing the pane emits only the global
// hideEntityView. Without this watcher the double-click pin selection — the checkmark
// icon — survives the close and sticks to the pin.
export function* onHideEntityView() {
  const selectedPin: ReturnType<typeof getRouteSelectedPin> = yield select(getRouteSelectedPin);
  if (!selectedPin) {
    return;
  }
  // In-pane back-navigation closes and reopens the view in one synchronous dispatch
  // chain (PreviewEditPanes.handleBack: hideEntityView + showEntityView). Yield one
  // tick and keep the selection when the pane turns out to still be open.
  yield delay(0);
  const previewReopened: boolean = yield select(isEntityViewVisible);
  if (!previewReopened) {
    yield put(setRouteSelectedPin(undefined));
    yield put(setRoutePinHover(undefined));
  }
}

export function* onFetchRoutePinsSuccess() {
  try {
    const lasso: boolean = yield select(isRouteLassoMode);
    if (!lasso) {
      return;
    }
    const paths: GeoPath[] = yield select(getRouteLassoPaths);
    if (paths.length === 0) {
      return;
    }
    const mapEntries: CategorizedMapEntries = yield select(getRouteCategorizedMapEntries);
    const externalMapEntries: CategorizedMapEntries = yield select(
      getRouteCategorizedExternalMapEntries
    );
    yield put(
      updateRouteLassoClusters([
        ...(mapEntries.clusters ?? []),
        ...(externalMapEntries.clusters ?? []),
      ])
    );
    yield put(refreshRouteLassoMode());
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* pinsSaga() {
  yield all([
    takeLatest(initializeRouteViewport.request, onInitializeRouteViewport),
    takeLatest(persistRouteViewport, onPersistRouteViewport),
    takeLatest(fetchRouteViewPins.request, onFetchRouteViewPins),
    takeLatest(fetchPins.request, onFetchPins),
    takeLatest([fetchRouteViewPins.success, fetchPins.success], onFetchRoutePinsSuccess),
    takeLatest(focusPin, onFocusPin),
    takeLatest(hideEntityView, onHideEntityView),
  ]);
}
